import { Alert, Box, Skeleton, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_SORT,
  EMPTY_SELECTION,
  type RegisterSelection,
  type RegisterSort,
} from "../api/spofRegister";
import SpofMitigationsDialog from "../components/SpofMitigationsDialog";
import SpofRegisterFilters from "../components/SpofRegisterFilters";
import SpofRegisterTable from "../components/SpofRegisterTable";
import {
  useRegisterFilterOptions,
  useRegisterSearch,
} from "../hooks/useSpofRegister";
import PageHeader from "../layout/PageHeader";
import type { SpofRecord } from "../types";

export default function SpofRegisterPage() {
  const [params] = useSearchParams();
  // The P&ID explorer links here with the SPOF id it wants shown.
  const [selection, setSelection] = useState<RegisterSelection>({
    ...EMPTY_SELECTION,
    keyword: params.get("keyword"),
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<RegisterSort>(DEFAULT_SORT);
  const [mitigationsOf, setMitigationsOf] = useState<SpofRecord | null>(null);

  const options = useRegisterFilterOptions(selection).data;
  const result = useRegisterSearch(selection, sort, page, pageSize).data;

  function select(next: RegisterSelection) {
    setSelection(next);
    setPage(1);
  }

  function sortBy(next: RegisterSort) {
    setSort(next);
    setPage(1);
  }

  return (
    <Box>
      <PageHeader
        title="SPOF Register Explorer"
        trail={[
          { label: "Fleet overview", to: "/dashboard" },
          { label: "SPOF Register" },
        ]}
      />

      {!options || !result ? (
        <Skeleton variant="rounded" height={520} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <SpofRegisterFilters
            options={options}
            selection={selection}
            onChange={select}
          />

          {result.total === 0 ? (
            <Alert severity="info">No SPOF matches the current filters.</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {result.total} records
              </Typography>
              <SpofRegisterTable
                records={result.records}
                total={result.total}
                page={page}
                pageSize={pageSize}
                sort={sort}
                onSortChange={sortBy}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                onShowMitigations={setMitigationsOf}
              />
            </>
          )}

          <SpofMitigationsDialog
            record={mitigationsOf}
            onClose={() => setMitigationsOf(null)}
          />
        </Box>
      )}
    </Box>
  );
}
